import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Product from '../Product/Product';
import styles from './ProductList.module.css';

/**
 * ProductList - Displays a filterable, searchable list of products
 * 
 * @param {string} seriesId - Series ID to fetch products from
 * @param {string} thumbSize - Thumbnail size (default: "256")
 * @param {Object} filters - Filter rules in format { key: value } to match exact values
 * @param {string} searchValue - Search term to match across multiple columns
 * @param {Array} searchColumns - Array of column names to search in (default: ['name', 'color', 'description'])
 * @param {Function} onProductSelect - Callback when product is selected
 * @param {string} titleAttribute - Product attribute to use as title (default: 'color')
 * @param {string} descriptionAttribute - Product attribute to use as description (default: 'name')
 * @param {Function} transformTitle - Optional function to transform title text before display
 * @param {Function} transformDescription - Optional function to transform description text before display
 * @param {string} className - Additional CSS class
 */
export default function ProductList({ 
    seriesId, 
    thumbSize = "256",
    focus = 500,
    filters = {}, 
    searchValue = "",
    searchColumns = ['name', 'color', 'description', 'manufacturerSeriesName'],
    onProductSelect,
    titleAttribute = 'color',
    descriptionAttribute = 'name',
    transformTitle,
    transformDescription,
    className = "",
    gridClassName = "",
    priorityIds = []
}) {
    const [productData, setProductData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedProductId, setSelectedProductId] = useState(null);

    // Fetch product data
    useEffect(() => {
        if (!seriesId) return;

        setLoading(true);
        setError(null);

        const URL = `https://lizard.chameleonpower.com/CDN/ProductsBySeries/${seriesId}.json`;
        
        fetch(URL)
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`Failed to fetch products: ${response.status}`);
                }
                return response.json();
            })
            .then((data) => {
                setProductData(Array.isArray(data) ? data : []);
                setLoading(false);
            })
            .catch((error) => {
                console.error('Error fetching products:', error);
                setError(error.message);
                setLoading(false);
            });
    }, [seriesId]);

    // Apply filters
    const applyFilters = (products) => {
        if (!filters || Object.keys(filters).length === 0) {
            return products;
        }

        return products.filter(product => {
            // Check if product matches all filter rules
            return Object.entries(filters).every(([key, value]) => {
                const productValue = product[key];
                
                // Handle null/undefined
                if (productValue === null || productValue === undefined) {
                    return false;
                }
                
                // Convert to string for comparison
                return String(productValue).toLowerCase() === String(value).toLowerCase();
            });
        });
    };

    // Apply search
    const applySearch = (products) => {
        if (!searchValue || searchValue.trim() === '') {
            return products;
        }

        const searchLower = searchValue.toLowerCase().trim();

        return products.filter(product => {
            // Check if search term matches any of the specified columns (partial match)
            return searchColumns.some(column => {
                const columnValue = product[column];
                
                // Handle null/undefined
                if (columnValue === null || columnValue === undefined) {
                    return false;
                }
                
                // Convert to string and check for partial match
                return String(columnValue).toLowerCase().includes(searchLower);
            });
        });
    };

    // Get filtered and searched products
    const getFilteredProducts = () => {
        let filtered = productData;
        filtered = applyFilters(filtered);
        filtered = applySearch(filtered);
        
        // Sort by priority IDs if provided
        if (priorityIds && priorityIds.length > 0) {
            filtered = [...filtered].sort((a, b) => {
                const aIndex = priorityIds.indexOf(a.id);
                const bIndex = priorityIds.indexOf(b.id);
                
                // If both are priority items, sort by their priority order
                if (aIndex !== -1 && bIndex !== -1) {
                    return aIndex - bIndex;
                }
                // Priority items come first
                if (aIndex !== -1) return -1;
                if (bIndex !== -1) return 1;
                // Non-priority items keep original order
                return 0;
            });
        }
        
        return filtered;
    };

    const filteredProducts = getFilteredProducts();

    // Handle product selection
    const handleProductSelect = (id) => {
        const selectedProduct = productData.find(product => product.id === id);
        if (selectedProduct && onProductSelect) {
            setSelectedProductId(id);
            onProductSelect(selectedProduct);
        }
    };

    // Render loading state
    if (loading) {
        return (
            <div className={`${styles.productList} ${className}`}>
                <div className={styles.message}>Loading products...</div>
            </div>
        );
    }

    // Render error state
    if (error) {
        return (
            <div className={`${styles.productList} ${className}`}>
                <div className={styles.errorMessage}>Error: {error}</div>
            </div>
        );
    }

    // Render product list
    return (
        <div className={`${styles.productList} ${className}`} data-series={seriesId}>
            {filteredProducts.length > 0 ? (
                <ul className={`${styles.productGrid} ${gridClassName}`}>
                    {filteredProducts.map(product => {
                        const title = product[titleAttribute] || '';
                        const description = product[descriptionAttribute] || '';
                        
                        return (
                            <Product
                                key={product.id}
                                id={product.id}
                                specifier={product.specifier}
                                decalID={product.description}
                                focus={focus}
                                size={thumbSize}
                                title={transformTitle ? transformTitle(title) : title}
                                description={transformDescription ? transformDescription(description) : description}
                                series={seriesId}
                                onSelect={handleProductSelect}
                                isActive={product.id === selectedProductId}
                            />
                        );
                    })}
                </ul>
            ) : (
                <div className={styles.emptyMessage}>
                    {searchValue || Object.keys(filters).length > 0 
                        ? 'No products match your criteria.' 
                        : 'No products available.'}
                </div>
            )}
        </div>
    );
}

ProductList.propTypes = {
    seriesId: PropTypes.string.isRequired,
    thumbSize: PropTypes.string,
    focus: PropTypes.number,
    filters: PropTypes.object,
    searchValue: PropTypes.string,
    searchColumns: PropTypes.arrayOf(PropTypes.string),
    onProductSelect: PropTypes.func,
    titleAttribute: PropTypes.string,
    descriptionAttribute: PropTypes.string,
    transformTitle: PropTypes.func,
    transformDescription: PropTypes.func,
    className: PropTypes.string,
    gridClassName: PropTypes.string,
    priorityIds: PropTypes.array,
};